import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import { ROUTES } from '../constants/appRoutes';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import {
    formatIsDefaultLabel,
    formatTemplateCreatedDate,
    formatTemplateSourceLabel,
    fetchMenuTemplates,
    normalizeIsDefault,
    normalizeTemplateSource,
} from './dailyMenuApi';
import MenuTemplateItemsEditor from './MenuTemplateItemsEditor';
import JreatsStatewideMenuBrowser from './JreatsStatewideMenuBrowser';
import OrdersTablePanel from './OrdersTablePanel';
import OrdersTablePagination from './OrdersTablePagination';
import OrdersSortableTh from './OrdersSortableTh';
import { buildSortHandlers, parseDateSortValue, sortList, str } from './tableSortUtils';
import {
    fetchMenuTemplateById,
    itemToCreatePayload,
    parseMealsCsv,
    updateMenuTemplate,
} from './menuTemplateItemUtils';
import { confirmAction, showUndoToast } from './ui/feedback';
import './css/Menu.css';
import './css/Inventory.css';
import './css/DailyMenu.css';
import './css/OrdersTablePagination.css';
const DAYS_OF_WEEK = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];

function getTodayDayOfWeek() {
    const names = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
    ];
    return names[new Date().getDay()] ?? 'Monday';
}

function authHeaders() {
    const token = localStorage.getItem('Token');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDayOfWeekDisplay(day) {
    if (day == null || String(day).trim() === '') return '';
    const s = String(day).trim().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatStatusDisplay(status) {
    if (status == null || String(status).trim() === '') return '';
    const s = String(status).trim().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function isActiveStatus(status) {
    return String(status ?? '').trim().toLowerCase() === 'active';
}

function getTemplateSortValue(row, sortKey) {
    switch (sortKey) {
        case 'templateName':
            return str(row.templateName);
        case 'dayOfWeek':
            return str(row.dayOfWeek);
        case 'isDefault':
            return normalizeIsDefault(row.isDefault) ? 1 : 0;
        case 'status':
            return str(row.status);
        case 'createdDate':
            return parseDateSortValue(row.createdDate ?? row.createdAt);
        default:
            return '';
    }
}

/** POST /restaurants/menu-templates */
async function createMenuTemplate(payload) {
    return axiosRestaurantInstance.post(RESTAURANT_API_PATHS.restaurantsMenuTemplates, payload, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
}

/** DELETE /restaurants/menu-templates/:templateId — soft delete (status → INACTIVE) */
async function deleteMenuTemplate(templateId) {
    return axiosRestaurantInstance.delete(RESTAURANT_API_PATHS.restaurantMenuTemplateById(templateId), {
        headers: authHeaders(),
    });
}

function apiDayToFormDay(day) {
    return formatDayOfWeekDisplay(day) || 'Monday';
}

function apiStatusToFormStatus(status) {
    return formatStatusDisplay(status) || 'Active';
}

const MenuTemplate = ({ restaurantId, hideTitle = false, onBack, lockedSource = null }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const normalizedLockedSource = lockedSource
        ? normalizeTemplateSource(lockedSource)
        : null;
    const isSourceLocked = normalizedLockedSource != null;
    const [showTemplateForm, setShowTemplateForm] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [templateName, setTemplateName] = useState('');
    const [dayOfWeek, setDayOfWeek] = useState(getTodayDayOfWeek);
    const [status, setStatus] = useState('Active');
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [templateMenuSource, setTemplateMenuSource] = useState(normalizedLockedSource || 'MYEATS');
    const [formTemplateSource, setFormTemplateSource] = useState(normalizedLockedSource || 'MYEATS');
    const [formIsDefault, setFormIsDefault] = useState(true);
    const [formItems, setFormItems] = useState([]);
    const [loadingFormItems, setLoadingFormItems] = useState(false);
    const [templateTableSearch, setTemplateTableSearch] = useState('');
    const [templatePage, setTemplatePage] = useState(1);
    const [templatePageSize, setTemplatePageSize] = useState(10);
    const [templateSort, setTemplateSort] = useState(null);

    const filteredTemplates = useMemo(() => {
        const q = templateTableSearch.trim().toLowerCase();
        if (!q) return templates;
        return templates.filter((t) =>
            [
                t.templateName,
                t.dayOfWeek,
                t.status,
                formatTemplateCreatedDate(t.createdDate ?? t.createdAt),
                formatIsDefaultLabel(t.isDefault),
            ].some((v) => String(v ?? '').toLowerCase().includes(q))
        );
    }, [templates, templateTableSearch]);

    useEffect(() => {
        setTemplatePage(1);
        setTemplateSort(null);
    }, [templateTableSearch, templateMenuSource]);

    const sortedTemplates = useMemo(
        () => sortList(filteredTemplates, templateSort, getTemplateSortValue),
        [filteredTemplates, templateSort]
    );

    const { handleSortAsc: handleTemplateSortAsc, handleSortDesc: handleTemplateSortDesc } =
        buildSortHandlers(setTemplateSort, () => setTemplatePage(1));

    const templateTotalPages = Math.max(1, Math.ceil(sortedTemplates.length / templatePageSize));
    const templatePageSafe = Math.min(Math.max(1, templatePage), templateTotalPages);

    useEffect(() => {
        setTemplatePage((p) => Math.min(p, templateTotalPages));
    }, [templateTotalPages, templatePageSize]);

    const templatePageStart = (templatePageSafe - 1) * templatePageSize;
    const pagedTemplates = sortedTemplates.slice(
        templatePageStart,
        templatePageStart + templatePageSize
    );

    const handleTemplatePageChange = (nextPage) => {
        setTemplatePage(nextPage);
    };

    const handleTemplatePageSizeChange = (nextSize) => {
        setTemplatePageSize(nextSize);
        setTemplatePage(1);
    };

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || 'null');
        } catch (e) {
            return null;
        }
    }, []);

    const loadTemplates = useCallback(async () => {
        if (!restaurantId) {
            setTemplates([]);
            return;
        }
        setLoading(true);
        try {
            const list = await fetchMenuTemplates(restaurantId, {
                templateSource: templateMenuSource,
            });
            setTemplates(list);
        } catch (err) {
            console.error('Error fetching menu templates:', err?.response?.data || err?.message);
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    }, [restaurantId, templateMenuSource]);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    useEffect(() => {
        if (!normalizedLockedSource) return;
        setTemplateMenuSource(normalizedLockedSource);
        setFormTemplateSource(normalizedLockedSource);
    }, [normalizedLockedSource]);

    const handleOpenTemplateForm = (e) => {
        e.preventDefault();
        setEditingTemplate(null);
        resetTemplateForm();
        setShowTemplateForm(true);
    };

    const resetTemplateForm = () => {
        setTemplateName('');
        setDayOfWeek(getTodayDayOfWeek());
        setStatus('Active');
        setFormTemplateSource(templateMenuSource);
        setFormIsDefault(true);
        setFormItems([]);
        setLoadingFormItems(false);
    };

    const handleCloseTemplateForm = () => {
        setShowTemplateForm(false);
        setEditingTemplate(null);
        resetTemplateForm();
    };

    const loadFormItems = useCallback(async (templateId) => {
        if (!templateId) {
            setFormItems([]);
            return;
        }
        setLoadingFormItems(true);
        try {
            const data = await fetchMenuTemplateById(templateId);
            setFormItems(Array.isArray(data.items) ? data.items : []);
        } catch (err) {
            console.error('Error loading template items:', err?.response?.data || err?.message);
            setFormItems([]);
        } finally {
            setLoadingFormItems(false);
        }
    }, []);

    const handleEditTemplate = (template) => {
        setEditingTemplate(template);
        setTemplateName(template.templateName ?? '');
        setDayOfWeek(apiDayToFormDay(template.dayOfWeek));
        setStatus(apiStatusToFormStatus(template.status));
        setFormTemplateSource(normalizeTemplateSource(template.templateSource));
        setFormIsDefault(normalizeIsDefault(template.isDefault));
        setFormItems([]);
        setShowTemplateForm(true);
        loadFormItems(template.templateId);
    };

    const handleDeleteTemplate = async (template) => {
        const templateId = template.templateId;
        if (!templateId) return;

        const confirmed = await confirmAction({
            title: 'Delete template?',
            message: `"${template.templateName}" will be set to inactive.`,
            confirmLabel: 'Delete',
            cancelLabel: 'Keep',
            danger: true,
        });
        if (!confirmed) return;

        const prevTemplates = templates;
        setTemplates((list) => list.filter((t) => t.templateId !== templateId));

        showUndoToast({
            message: `Deleted "${template.templateName}"`,
            onUndo: () => setTemplates(prevTemplates),
            onCommit: async () => {
                try {
                    await deleteMenuTemplate(templateId);
                    await loadTemplates();
                } catch (err) {
                    const msg =
                        err.response?.data?.message ||
                        err.response?.data?.error ||
                        err.message ||
                        'Failed to delete template.';
                    setTemplates(prevTemplates);
                    alert(`Error: ${msg}`);
                }
            },
        });
    };

    const isEditMode = Boolean(editingTemplate?.templateId);
    const hideStatusOnEdit = isEditMode && isActiveStatus(editingTemplate?.status);

    const handleSaveTemplate = async (e) => {
        e.preventDefault();
        if (!restaurantId) {
            alert('No restaurant selected.');
            return;
        }
        if (!templateName.trim()) {
            alert('Please enter a template name.');
            return;
        }

        if (!isEditMode && status === 'Inactive' && formItems.length > 0) {
            alert(
                'Inactive templates cannot include items. Set status to Active or remove items.'
            );
            return;
        }

        for (const item of formItems) {
            if (parseMealsCsv(item.meal).length === 0) {
                alert(
                    `Select at least one meal type for "${item.foodItemName || 'a template item'}".`
                );
                return;
            }
        }

        const userId = Number(user?.userId) || Number(restaurantId);
        const editingActive = isEditMode && isActiveStatus(editingTemplate?.status);

        setSaving(true);
        try {
            let response;
            if (editingTemplate?.templateId) {
                const updateStatus = editingActive
                    ? String(editingTemplate.status ?? 'ACTIVE').trim().toUpperCase()
                    : status.toUpperCase();
                response = await updateMenuTemplate(editingTemplate.templateId, {
                    restaurantId: Number(restaurantId),
                    dayOfWeek,
                    templateName: templateName.trim(),
                    templateSource: formTemplateSource,
                    isDefault: formIsDefault,
                    status: updateStatus,
                    updatedById: userId,
                });
            } else {
                response = await createMenuTemplate({
                    restaurantId: Number(restaurantId),
                    dayOfWeek: dayOfWeek.toUpperCase(),
                    status: status.toUpperCase(),
                    templateName: templateName.trim(),
                    templateSource: formTemplateSource,
                    isDefault: formIsDefault,
                    createdById: userId,
                    items: formItems.map((item) => itemToCreatePayload(item, userId)),
                });
            }
            const data = response.data || {};
            alert(
                data.message ||
                    (editingTemplate ? 'Menu template updated.' : 'Menu template created successfully.')
            );
            setTemplateMenuSource(normalizeTemplateSource(formTemplateSource));
            handleCloseTemplateForm();
            await loadTemplates();
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                (editingTemplate ? 'Failed to update template.' : 'Failed to create template.');
            alert(`Error: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="menu menu-template">
            <div className={`menu-heading${hideTitle ? ' menu-heading--templates-only' : ''}`}>
                <div className="menu-template-detail-title">
                    <h2>Menu Templates</h2>
                    {normalizedLockedSource === 'JREATS' ? (
                        <p className="menu-template-jreats-state-hint">
                            Build templates from statewide Jr Eats dishes, then publish them on Daily Menu.
                        </p>
                    ) : null}
                </div>
                <div className="menu-heading-actions">
                    {!isSourceLocked && (
                        <div className="menu-template-source-actions">
                            <button
                                type="button"
                                className={`menu-toolbar-btn menu-template-source-btn${
                                    templateMenuSource === 'DINEIN' ? ' menu-toolbar-btn--active' : ''
                                }`}
                                onClick={() => setTemplateMenuSource('DINEIN')}
                            >
                                Dine-In
                            </button>
                            <button
                                type="button"
                                className={`menu-toolbar-btn menu-template-source-btn${
                                    templateMenuSource === 'MYEATS' ? ' menu-toolbar-btn--active' : ''
                                }`}
                                onClick={() => setTemplateMenuSource('MYEATS')}
                            >
                                MYEATS
                            </button>
                            <button
                                type="button"
                                className={`menu-toolbar-btn menu-template-source-btn${
                                    templateMenuSource === 'JREATS' ? ' menu-toolbar-btn--active' : ''
                                }`}
                                onClick={() => setTemplateMenuSource('JREATS')}
                            >
                                JREATS
                            </button>
                        </div>
                    )}
                    <div className="addmenu-button">
                        {normalizedLockedSource === 'JREATS' ? (
                            <JreatsStatewideMenuBrowser
                                restaurantId={restaurantId}
                                triggerLabel="View statewide dishes"
                                compact
                            />
                        ) : null}
                        <button type="button" className="add-button" onClick={handleOpenTemplateForm}>
                            Add Template
                        </button>
                    </div>
                </div>
            </div>
            {showTemplateForm && (
                <div
                    className="inventory-modal-overlay"
                    onClick={handleCloseTemplateForm}
                    role="presentation"
                >
                    <div
                        className="inventory-modal menu-template-modal menu-template-modal--with-items"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="menu-template-modal-title"
                    >
                        <div className="inventory-modal-header menu-template-modal-header">
                            <h4 id="menu-template-modal-title">
                                {isEditMode ? 'Edit Template' : 'Add Template'}
                            </h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                onClick={handleCloseTemplateForm}
                                aria-label="Close"
                                disabled={saving}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <form
                            className="inventory-form menu-template-modal-form"
                            onSubmit={handleSaveTemplate}
                        >
                            <div className="inventory-form-group">
                                <label htmlFor="template-name" className="menu-form-label--required">
                                    Template name
                                </label>
                                <input
                                    id="template-name"
                                    type="text"
                                    className="add-menu-form-field"
                                    placeholder="e.g. Tuesday Template"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    required
                                    autoFocus
                                />
                            </div>
                            {!isSourceLocked && (
                                <div className="inventory-form-group">
                                    <label htmlFor="template-source">Template source</label>
                                    <div className="inventory-native-select-wrap">
                                        <select
                                            id="template-source"
                                            className="add-menu-form-field inventory-select-native"
                                            value={formTemplateSource}
                                            onChange={(e) =>
                                                setFormTemplateSource(
                                                    normalizeTemplateSource(e.target.value)
                                                )
                                            }
                                        >
                                            <option value="DINEIN">Dine-In</option>
                                            <option value="MYEATS">MYEATS</option>
                                            <option value="JREATS">JREATS</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                            <div className="inventory-form-group">
                                <label htmlFor="template-day">Day of week</label>
                                <select
                                    id="template-day"
                                    className="add-menu-form-field"
                                    value={dayOfWeek}
                                    onChange={(e) => setDayOfWeek(e.target.value)}
                                >
                                    {DAYS_OF_WEEK.map((day) => (
                                        <option key={day} value={day}>
                                            {day}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="inventory-form-group">
                                <label htmlFor="template-is-default">Default for this day</label>
                                <div className="inventory-native-select-wrap">
                                    <select
                                        id="template-is-default"
                                        className="add-menu-form-field inventory-select-native"
                                        value={formIsDefault ? '1' : '0'}
                                        onChange={(e) =>
                                            setFormIsDefault(normalizeIsDefault(e.target.value))
                                        }
                                    >
                                        <option value="0">No</option>
                                        <option value="1">Yes</option>
                                    </select>
                                </div>
                            </div>
                            {!hideStatusOnEdit && (
                                <div className="inventory-form-group">
                                    <label htmlFor="template-status">Status</label>
                                    <select
                                        id="template-status"
                                        className="add-menu-form-field"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                            )}
                            <div className="menu-template-modal-form-span-2">
                            <MenuTemplateItemsEditor
                                restaurantId={restaurantId}
                                menuSource={formTemplateSource}
                                items={formItems}
                                onItemsChange={setFormItems}
                                templateId={editingTemplate?.templateId ?? null}
                                user={user}
                                loading={loadingFormItems}
                                disabled={saving}
                                showTitle={false}
                                title="Template items"
                                headerClassName="menu-template-form-items-header"
                                emptyMessage="No items added yet. Click Add from menu to select items."
                            />
                            </div>
                            <div className="inventory-form-actions menu-template-modal-actions menu-template-modal-form-span-2">
                                <button type="submit" className="add-button" disabled={saving}>
                                    {saving
                                        ? 'Saving…'
                                        : isEditMode
                                          ? 'Update Template'
                                          : 'Create Template'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <OrdersTablePanel
                searchValue={templateTableSearch}
                onSearchChange={setTemplateTableSearch}
                resultCount={sortedTemplates.length}
                resultLabel="template"
                searchAriaLabel="Search menu templates"
            >
            <table className="menu-table">
                    <thead>
                        <tr>
                            <OrdersSortableTh label="Template Name" sortKey="templateName" activeSortKey={templateSort?.key} sortDirection={templateSort?.direction} onSortAsc={handleTemplateSortAsc} onSortDesc={handleTemplateSortDesc} />
                            <OrdersSortableTh label="Day" sortKey="dayOfWeek" activeSortKey={templateSort?.key} sortDirection={templateSort?.direction} onSortAsc={handleTemplateSortAsc} onSortDesc={handleTemplateSortDesc} />
                            <OrdersSortableTh label="Default (day)" sortKey="isDefault" activeSortKey={templateSort?.key} sortDirection={templateSort?.direction} onSortAsc={handleTemplateSortAsc} onSortDesc={handleTemplateSortDesc} />
                            <OrdersSortableTh label="Status" sortKey="status" activeSortKey={templateSort?.key} sortDirection={templateSort?.direction} onSortAsc={handleTemplateSortAsc} onSortDesc={handleTemplateSortDesc} />
                            <OrdersSortableTh label="Created On" sortKey="createdDate" activeSortKey={templateSort?.key} sortDirection={templateSort?.direction} onSortAsc={handleTemplateSortAsc} onSortDesc={handleTemplateSortDesc} />
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="menu-table-empty-cell menu-empty-filtered">
                                    Loading templates…
                                </td>
                            </tr>
                        ) : filteredTemplates.length > 0 ? (
                            pagedTemplates.map((t, index) => (
                                <tr key={t.templateId ?? `${t.templateName}-${index}`}>
                                    <td>
                                        <button
                                            type="button"
                                            className="inventory-item-name-link"
                                            onClick={() => {
                                                const params = new URLSearchParams();
                                                params.set(
                                                    'tab',
                                                    `restaurants/mytemplates/${t.templateId}`
                                                );
                                                const channel = searchParams.get('channel');
                                                if (channel) params.set('channel', channel);
                                                navigate(`${ROUTES.businessAdmin}?${params.toString()}`);
                                            }}
                                        >
                                            {t.templateName}
                                        </button>
                                    </td>
                                    <td>{formatDayOfWeekDisplay(t.dayOfWeek)}</td>
                                    <td>{formatIsDefaultLabel(t.isDefault)}</td>
                                    <td>
                                        <span
                                            className={`menu-template-status-badge menu-template-status-badge--${
                                                isActiveStatus(t.status) ? 'active' : 'inactive'
                                            }`}
                                        >
                                            {formatStatusDisplay(t.status)}
                                        </span>
                                    </td>
                                    <td className="menu-table-cell-muted">
                                        {formatTemplateCreatedDate(t.createdDate)}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="action-button"
                                            title="Edit template"
                                            onClick={() => handleEditTemplate(t)}
                                        >
                                            <FaEdit />
                                        </button>
                                        {isActiveStatus(t.status) && (
                                            <button
                                                type="button"
                                                className="action-button delete-button"
                                                title="Deactivate template"
                                                onClick={() => handleDeleteTemplate(t)}
                                            >
                                                <FaTrash />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="menu-table-empty-cell menu-empty-filtered">
                                    {templateTableSearch.trim()
                                        ? 'No templates match your search.'
                                        : 'No templates found.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </OrdersTablePanel>

            {!loading && filteredTemplates.length > 0 ? (
                <OrdersTablePagination
                    currentPage={templatePageSafe}
                    totalPages={templateTotalPages}
                    totalItems={sortedTemplates.length}
                    pageSize={templatePageSize}
                    onPageChange={handleTemplatePageChange}
                    onPageSizeChange={handleTemplatePageSizeChange}
                    itemLabel="templates"
                    ariaLabel="Menu templates table pagination"
                />
            ) : null}
        </div>
    );
};

export default MenuTemplate;
